import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Alert, Modal, StyleSheet, Platform } from 'react-native';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useUserTierAndPoints } from '../hooks/useUserTierAndPoints';
import PassCard from './PassCard';
import PassClaimStatusWrapper from './PassClaimStatusWrapper';
import GiftPassWorkflow from './GiftPassWorkflow';
import PassFilters from './PassFilters';
import { useUnusedPasses, getPassVenueNamesLine } from '../utils/passFiltering';
import { useNavigation } from '../utils/NavigationContext';
import { fontFamily } from '../constants/fonts';
import { colors } from '../constants/theme';

const PassesComponent = ({ 
  restaurantId, 
  userDoc, 
  getUserMembership, 
  selectedRestaurant,
  onUsePass, // New prop for handling pass use
  onCloseUsePassModal, // New prop for handling modal close
  forceExpanded = false // New prop to force expanded state
}) => {
  const { setShowDownloadAppModal } = useNavigation();

  // Use the new endpoint that gets all passes including claimed/gifted ones
  const allPasses = useQuery(
    api.getAvailablePasses.getAllPassesForUser,
    userDoc?._id ? { userId: userDoc._id, restaurantId } : "skip"
  );

  const claimPass = useMutation(api.passActions.claimPass);
  const usePass = useMutation(api.passActions.usePass);
  const cantGo = useMutation(api.passActions.cantGo);

  // Query for user's pass claims for the current week (or all time if needed)
  const userPassClaims = useQuery(
    userDoc?._id ? api.getAvailablePasses.getUserPassClaimsForCurrentWeekAll : "skip",
    userDoc?._id ? { userId: userDoc._id } : "skip"
  );

  const [isPassesExpanded, setIsPassesExpanded] = useState(false);
  
  // Use forceExpanded if provided, otherwise use local state
  const shouldShowContent = forceExpanded || isPassesExpanded;
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    claimed: false,
    gifted: false
  });
  
  // Gift pass workflow state (same as MapScreen and ProfileScreen)
  const [isGiftPassWorkflowVisible, setIsGiftPassWorkflowVisible] = useState(false);
  const [selectedPassForGift, setSelectedPassForGift] = useState(null);

  // Get user's tier and points using the new hook
  const { userPoints, currentTier, pointsNeededForNextTier, nextTier } = useUserTierAndPoints(selectedRestaurant);

  // Get user's membership for this restaurant (fallback)
  const userMembership = getUserMembership ? getUserMembership(selectedRestaurant?.name) : null;
  const userTier = currentTier || userMembership?.tier || "Discovery";

  // Get all passes with accessibility info and claim status
  const processedPasses = React.useMemo(() => {
    if (!allPasses || !userTier) return [];

    const tierHierarchy = {
      "Discovery": 0,
      "Silver": 1,
      "Gold": 2,
      "Platinum": 3,
      "Diamond": 4
    };

    const userTierLevel = tierHierarchy[userTier] || 0;

    return allPasses.map(pass => {
      if (!pass.tierRequired) {
        return { ...pass, isAccessible: true, pointsNeeded: 0 };
      }
      
      const requiredTierLevel = tierHierarchy[pass.tierRequired] || 0;
      const isAccessible = userTierLevel >= requiredTierLevel;
      
      // Calculate points needed for this pass
      const requiredTier = selectedRestaurant?.membershipTiers?.find(tier => tier.tier === pass.tierRequired);
      const pointsNeeded = requiredTier ? Math.max(0, requiredTier.checkinRequirement - userPoints) : 0;
      
      return { 
        ...pass, 
        isAccessible, 
        pointsNeeded,
        requiredPoints: requiredTier?.checkinRequirement || 0
      };
    });
  }, [allPasses, userTier, userPoints, selectedRestaurant]);

  // Filter to only show unused passes (unless we're filtering for claimed/gifted)
  const categorizedPasses = useUnusedPasses(processedPasses, userPassClaims, {
    selectedRestaurant,
    userTier,
    userPoints,
    userId: userDoc?._id
  });

  // When filtering for claimed/gifted, we need to include ALL passes, not just unused ones
  const accessiblePasses = React.useMemo(() => {
    if (filters.claimed || filters.gifted) {
      // When any filter is ON: show unused passes + filtered passes
      const unusedPasses = [
        ...(categorizedPasses.down || []).map(pass => ({ ...pass, category: 'down' })),
        ...(categorizedPasses.location || []).map(pass => ({ ...pass, category: 'location' }))
      ];
      
      let filteredPasses = [];
      
      if (filters.claimed) {
        const claimedPasses = processedPasses
          .filter(pass => pass.isClaimedThisWeek)
          .map(pass => ({ ...pass, category: 'claimed' }));
        filteredPasses = [...filteredPasses, ...claimedPasses];
      }
      
      if (filters.gifted) {
        const giftedPasses = processedPasses
          .filter(pass => pass.isGiftedThisWeek)
          .map(pass => ({ ...pass, category: 'gifted' }));
        filteredPasses = [...filteredPasses, ...giftedPasses];
      }
      
      // Combine unused passes with filtered passes
      return [...unusedPasses, ...filteredPasses];
    } else {
      // When no filters are ON: only show unused passes
      return [
        ...(categorizedPasses.down || []).map(pass => ({ ...pass, category: 'down' })),
        ...(categorizedPasses.location || []).map(pass => ({ ...pass, category: 'location' }))
      ];
    }
  }, [categorizedPasses, processedPasses, filters.claimed, filters.gifted]);

  // Filter to show passes that require next tier
  const nextTierPasses = React.useMemo(() => {
    return processedPasses.filter(pass => !pass.isAccessible);
  }, [processedPasses]);

  // Helper function to check if pass is currently claimable (same logic as PassCard)
  const isPassCurrentlyClaimable = (pass) => {
    if (pass.isActive === false) return false;
    
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Check if we're on the correct day of the week
    if (pass.startTime && pass.endTime) {
      const startDate = new Date(pass.startTime * 1000);
      const passDay = startDate.toLocaleDateString('en-US', { weekday: 'long' });
      
      if (currentDay !== passDay) {
        return false; // Wrong day of the week
      }
      
      // Check if current time is within the time window
      const unixTimestampToMinutes = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.getHours() * 60 + date.getMinutes();
      };

      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const startMinutes = unixTimestampToMinutes(pass.startTime);
      const endMinutes = unixTimestampToMinutes(pass.endTime);
      
      if (endMinutes < startMinutes) {
        // Time window spans midnight
        return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
      } else {
        // Normal time window within the same day
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }
    }
    
    // If no time constraints, check if it's the right day
    if (pass.weekStart) {
      const weekStartDate = new Date(pass.weekStart);
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 7);
      
      return now >= weekStartDate && now < weekEndDate;
    }
    
    // If no constraints at all, always available
    return true;
  };

  // Get passes with claim status (for display purposes)
  const passesWithClaimStatus = React.useMemo(() => {
    if (!accessiblePasses) return [];

    // Add currently claimable status to passes
    const passesWithStatus = accessiblePasses.map(pass => {
      const isCurrentlyClaimable = isPassCurrentlyClaimable(pass);
      return {
        ...pass,
        isCurrentlyClaimable
      };
    });

    // Apply filters
    let filteredPasses = passesWithStatus;

    // Note: claimedOrGifted filtering is already handled in accessiblePasses logic above
    // so we don't need to filter again here



    // Sort passes: claimable ones first, then by category (down, location)
    const sortedPasses = filteredPasses.sort((a, b) => {
      // First priority: claimable passes (currently claimable)
      if (a.isCurrentlyClaimable && !b.isCurrentlyClaimable) return -1;
      if (!a.isCurrentlyClaimable && b.isCurrentlyClaimable) return 1;
      
      // Second priority: category (down passes first)
      if (a.category === 'down' && b.category !== 'down') return 1;
      if (a.category !== 'down' && b.category === 'down') return -1;
      
      return 0;
    });

    return sortedPasses;
  }, [accessiblePasses, filters]);

  // Gift pass handlers (same as MapScreen and ProfileScreen)
  const handleGiftPass = async (pass) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to gift passes.");
      return;
    }

    if (Platform.OS === "web") {
      setShowDownloadAppModal(true);
      return;
    }

    setSelectedPassForGift(pass);
    setIsGiftPassWorkflowVisible(true);
  };

  const handleCloseGiftPassWorkflow = () => {
    setIsGiftPassWorkflowVisible(false);
    setSelectedPassForGift(null);
  };

  const handlePassClaim = async (pass, isWithinTimeframe) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to use passes.");
      return;
    }

    // If the pass is within timeframe, navigate directly to use pass screen
    if (isWithinTimeframe) {
      onUsePass(pass);
      return;
    }

    // If not within timeframe, proceed with normal claim flow
    try {
      const result = await claimPass({
        passId: pass._id,
        userId: userDoc._id,
        actionType: "down",
      });

      if (result.success) {
        Alert.alert("Success", `You're down for ${pass.title}!`);
      } else {
        Alert.alert("Error", result.error || "Failed to go down for pass");
      }
    } catch (error) {
      console.error("Error going down for pass:", error);
      Alert.alert("Error", "Failed to go down for pass");
    }
  };

  const handleUsePass = (pass) => {
    // Call the parent's onUsePass handler
    onUsePass(pass);
  };

  const handleCloseUsePassModal = () => {
    onCloseUsePassModal();
  };

  const handleCantGo = async (pass) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to use passes.");
      return;
    }

    try {
      const result = await cantGo({
        passId: pass._id,
        userId: userDoc._id,
      });

      if (result.success) {
        Alert.alert("Success", result.message || "Pass reservation cancelled.");
      } else {
        Alert.alert("Error", result.error || "Failed to cancel pass");
      }
    } catch (error) {
      console.error("Error cancelling pass:", error);
      Alert.alert("Error", "Failed to cancel pass");
    }
  };

  // Filter toggle handler
  const toggleFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const renderFilterModal = () => (
    <Modal
      visible={isFilterModalVisible}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setIsFilterModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Filter Passes</Text>
            <TouchableOpacity onPress={() => setIsFilterModalVisible(false)}>
              <Ionicons name="close" size={24} color={colors.brownMuted} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.modalBody}>
            <View style={styles.filterRow}>
              <Text style={styles.filterLabel}>Claimed</Text>
              <TouchableOpacity 
                onPress={() => toggleFilter('claimed')}
                style={[
                  styles.toggleTrack,
                  { backgroundColor: filters.claimed ? colors.brownMuted : colors.gray200 },
                ]}
              >
                <View
                  style={[
                    styles.toggleKnob,
                    { alignSelf: filters.claimed ? 'flex-end' : 'flex-start' },
                  ]}
                />
              </TouchableOpacity>
            </View>
            
            <View style={styles.filterRow}>
              <Text style={styles.filterLabel}>Gifted</Text>
              <TouchableOpacity 
                onPress={() => toggleFilter('gifted')}
                style={[
                  styles.toggleTrack,
                  { backgroundColor: filters.gifted ? colors.brownMuted : colors.gray200 },
                ]}
              >
                <View
                  style={[
                    styles.toggleKnob,
                    { alignSelf: filters.gifted ? 'flex-end' : 'flex-start' },
                  ]}
                />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (!passesWithClaimStatus || passesWithClaimStatus.length === 0) {
    return (
      <View>
        {!forceExpanded && (
          <View style={styles.headerBar}>
            <TouchableOpacity 
              onPress={() => setIsPassesExpanded(!isPassesExpanded)}
              style={styles.headerBarLeft}
            >
              <Text style={styles.headerTitle}>Passes</Text>
              <MaterialIcons 
                name={isPassesExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
                size={24} 
                color={colors.brownMuted} 
              />
            <TouchableOpacity 
              onPress={() => {
                Alert.alert(
                  "About Passes",
                  "Passes are special offers and deals that become available based on your loyalty tier and check-in frequency. Higher tiers unlock more exclusive passes!",
                  [{ text: "Got it", style: "default" }]
                );
              }}
              style={styles.headerInfoBtn}
            >
              <Ionicons name="information-circle" size={24} color={colors.black} />
            </TouchableOpacity>
          </TouchableOpacity>
          <TouchableOpacity 
            onPress={() => setIsFilterModalVisible(true)}
            style={styles.filterIconBtn}
          >
            <Ionicons name="filter" size={24} color={colors.brownMuted} />
          </TouchableOpacity>
        </View>
        )}

        {/* Unified Filter Component - always show when expanded */}
        {shouldShowContent && (
          <View style={styles.filtersWrap}>
            <PassFilters />
          </View>
        )}

        {shouldShowContent && (
          <View style={styles.contentPanel}>
            <View style={{ height: 1, backgroundColor: 'rgba(156, 107, 66, 0.5)' }} />
            
            {/* Empty State */}
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                No passes available
              </Text>
              <Text style={styles.emptySubtitle}>
                Check in more or reach higher loyalty tiers to unlock passes
              </Text>
              

            </View>

            {/* Next Tier Passes Section */}
            {nextTierPasses.length > 0 && (
              <View style={styles.nextTierSection}>
                <Text style={styles.nextTierHeading}>
                  Unlock More Passes
                </Text>
                {nextTierPasses.map((pass, index) => (
                  <View key={pass._id} style={styles.nextTierCardWrap}>
                    <PassCard
                      pass={pass}
                      showActionButtons={false}
                      showRestaurantName={true}
                      restaurantName={getPassVenueNamesLine(pass) || selectedRestaurant?.name}
                      isClaimedThisWeek={pass.isClaimedThisWeek}
                      isGiftedThisWeek={pass.isGiftedThisWeek}
                      isDownThisWeek={pass.isDownThisWeek}
                    />
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Gift Pass Workflow (same as MapScreen and ProfileScreen) */}
        <GiftPassWorkflow
          visible={isGiftPassWorkflowVisible}
          onClose={handleCloseGiftPassWorkflow}
          pass={selectedPassForGift}
          selectedRestaurant={selectedPassForGift?.restaurantDetails}
        />

        {renderFilterModal()}
      </View>
    );
  }

  return (
    <View>
      {!forceExpanded && (
        <View style={styles.headerBar}>
          <TouchableOpacity 
            onPress={() => setIsPassesExpanded(!isPassesExpanded)}
            style={styles.headerBarLeft}
          >
            <Text style={styles.headerTitle}>Passes</Text>
            {passesWithClaimStatus && passesWithClaimStatus.length > 0 && (
              <Text style={styles.headerCount}>
                ({passesWithClaimStatus.length})
              </Text>
            )}
            <MaterialIcons 
              name={isPassesExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
              size={24} 
              color={colors.brownMuted} 
            />
            <TouchableOpacity 
              onPress={() => {
                Alert.alert(
                  "About Passes",
                  "Passes are special offers and deals that become available based on your loyalty tier and check-in frequency. Higher tiers unlock more exclusive passes!",
                  [{ text: "Got it", style: "default" }]
                );
              }}
              style={styles.headerInfoBtn}
            >
              <Ionicons name="information-circle" size={24} color={colors.black} />
            </TouchableOpacity>
          </TouchableOpacity>
          <TouchableOpacity 
            onPress={() => setIsFilterModalVisible(true)}
            style={styles.filterIconBtn}
          >
            <Ionicons name="filter" size={24} color={colors.brownMuted} />
          </TouchableOpacity>
        </View>
      )}

      {/* Unified Filter Component - on next row */}
      {shouldShowContent && (
        <View style={styles.filtersWrap}>
          <PassFilters />
        </View>
      )}

      {shouldShowContent && (
        <View style={styles.contentPanel}>
          <View style={{ height: 1, backgroundColor: 'rgba(156, 107, 66, 0.5)' }} />
          
          {/* Passes List */}
          <View>
            {passesWithClaimStatus.map((pass, index) => (
              <PassClaimStatusWrapper key={pass._id} pass={pass} userDoc={userDoc}>
                {(wrapperClaimedStatus, actionType, isLoading) => (
                  <PassCard
                    pass={pass}
                    showClaimStatus={true}
                    isClaimedThisWeek={pass.isClaimedThisWeek}
                    isGiftedThisWeek={pass.isGiftedThisWeek}
                    isDownThisWeek={pass.isDownThisWeek}
                    actionType={actionType}
                    showActionButtons={true}
                    onGiftPress={handleGiftPass}
                    onClaimPress={handlePassClaim}
                    onUsePassPress={handleUsePass}
                    onCantGoPress={handleCantGo}
                    showRestaurantName={true}
                    restaurantName={getPassVenueNamesLine(pass) || selectedRestaurant?.name}
                    category={pass.category}
                  />
                )}
              </PassClaimStatusWrapper>
            ))}
          </View>

          {/* Next Tier Passes Section */}
          {nextTierPasses.length > 0 && (
            <View style={styles.nextTierSection}>
              <Text style={styles.nextTierHeading}>
                Unlock More Passes
              </Text>
              {nextTierPasses.map((pass, index) => (
                <View key={pass._id} style={styles.nextTierCardWrap}>
                  <PassCard
                    pass={pass}
                    showActionButtons={false}
                    showRestaurantName={true}
                    restaurantName={getPassVenueNamesLine(pass) || selectedRestaurant?.name}
                    isClaimedThisWeek={pass.isClaimedThisWeek}
                    isGiftedThisWeek={pass.isGiftedThisWeek}
                    isDownThisWeek={pass.isDownThisWeek}
                  />
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Gift Pass Workflow (same as MapScreen and ProfileScreen) */}
      <GiftPassWorkflow
        visible={isGiftPassWorkflowVisible}
        onClose={handleCloseGiftPassWorkflow}
        pass={selectedPassForGift}
        selectedRestaurant={selectedPassForGift?.restaurantDetails}
      />

      {renderFilterModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    padding: 8,
    backgroundColor: colors.white,
  },
  headerBarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: fontFamily.abhayaBold,
    marginLeft: 8,
    color: colors.black,
  },
  headerCount: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.gray600,
    marginLeft: 8,
  },
  headerInfoBtn: {
    marginLeft: 8,
  },
  filterIconBtn: {
    marginLeft: 4,
    padding: 4,
  },
  filtersWrap: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    backgroundColor: colors.white,
  },
  contentPanel: {
    backgroundColor: colors.white,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderWidth: 2,
    borderTopWidth: 0,
    borderColor: colors.gray100,
  },
  emptyState: {
    padding: 12,
    alignItems: 'center',
  },
  emptyTitle: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: colors.gray600,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: fontFamily.abhaya,
    color: colors.gray500,
    textAlign: 'center',
    marginBottom: 16,
  },
  nextTierSection: {
    marginTop: 24,
    paddingHorizontal: 12,
  },
  nextTierHeading: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: colors.gray600,
    marginBottom: 12,
  },
  nextTierCardWrap: {
    marginBottom: 12,
    opacity: 0.6,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 24,
    marginHorizontal: 16,
    width: 320,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontFamily: fontFamily.abhayaBold,
    color: colors.black,
  },
  modalBody: {
    gap: 16,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterLabel: {
    fontSize: 16,
    fontFamily: fontFamily.abhaya,
    color: colors.black,
  },
  toggleTrack: {
    width: 48,
    height: 24,
    borderRadius: 9999,
    padding: 4,
    justifyContent: 'center',
  },
  toggleKnob: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.white,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
});

export default PassesComponent;
